/** Canonical Book Lab editor routes. Writer and Canvas share one bookId. */

/**
 * Resolve a book document id from production API shapes.
 * Prefer canonical `id`, then Mongo `_id`, then `book_id`.
 */
export function resolveBookId(bookOrId) {
  if (bookOrId == null || bookOrId === "") return null;
  if (typeof bookOrId === "string" || typeof bookOrId === "number") {
    const s = String(bookOrId).trim();
    return s && s !== "undefined" && s !== "null" ? s : null;
  }
  const raw =
    bookOrId.id ??
    bookOrId._id ??
    bookOrId.book_id ??
    bookOrId.bookId ??
    null;
  if (raw == null || raw === "") return null;
  if (typeof raw === "object" && raw.$oid) return String(raw.$oid);
  if (typeof raw === "object" && typeof raw.toString === "function") {
    const s = raw.toString();
    if (s && s !== "[object Object]") return s;
  }
  const s = String(raw).trim();
  return s && s !== "undefined" && s !== "null" ? s : null;
}

/** @deprecated Prefer resolveBookEditorMode — kept for older callers. */
export function resolveBookLabView(requestedView) {
  return requestedView === "canvas" ? "canvas" : "writer";
}

/**
 * Resolve unified editor mode from URL search params.
 * Prefer explicit `mode`; map legacy `view=writer|canvas` for compatibility.
 */
export function resolveBookEditorMode(params) {
  const get = typeof params?.get === "function"
    ? (key) => params.get(key)
    : (key) => params?.[key];

  const explicitMode = get("mode");
  const legacyView = get("view");

  if (["write", "design", "canvas", "preview"].includes(explicitMode)) {
    return explicitMode;
  }
  if (legacyView === "canvas") return "canvas";
  if (legacyView === "writer") return "write";
  return "write";
}

export function bookLabEditorUrl(bookId, mode = "write") {
  const id = resolveBookId(bookId);
  const m = ["write", "design", "canvas", "preview"].includes(mode) ? mode : "write";
  if (!id) return `/book-lab/editor?mode=${m}`;
  return `/book-lab/editor?bookId=${encodeURIComponent(id)}&mode=${m}`;
}

export function bookLabWriterUrl(bookId) {
  // Legacy query kept for existing bookmarks; mode=write is canonical going forward.
  const id = resolveBookId(bookId);
  if (!id) return "/book-lab/editor?view=writer";
  return `/book-lab/editor?bookId=${encodeURIComponent(id)}&view=writer&mode=write`;
}

export function bookLabCanvasUrl(bookId) {
  const id = resolveBookId(bookId);
  if (!id) return "/book-lab/editor?view=canvas";
  return `/book-lab/editor?bookId=${encodeURIComponent(id)}&view=canvas&mode=canvas`;
}

export function bookLabHomeUrl() {
  return "/book-lab/editor/home";
}

/** Most recently edited book using canonical timestamps (not array position). */
export function pickLatestBook(books = []) {
  if (!books.length) return null;
  return [...books].sort((a, b) => {
    const ta = Date.parse(a.updated_at || a.modified_at || a.lastEditedAt || a.created_at || 0) || 0;
    const tb = Date.parse(b.updated_at || b.modified_at || b.lastEditedAt || b.created_at || 0) || 0;
    return tb - ta;
  })[0];
}
